import React, {
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Box, { BoxType } from 'component/Incentive/Message/Box';
import { TravelExpertWrapper } from 'component/Incentive/TravelExpert/Wrapper';
import StiftungWarentest from 'component/Incentive/Trust/Banner/StiftungWarentest';
import { PROMOTIONAL_VOUCHER_CONTAINER } from 'component/Incentive/Voucher/CouponVoyage/__config/constants';
import CouponVoyageBanner from 'component/Incentive/Voucher/CouponVoyage/Banner';
import Progress, { Status } from 'component/Layout/Loader/Progress';
import Pagination from 'component/Layout/Pagination';
import Hint from 'component/Search/Sort/Hint';
import Login from 'component/User/Login/Banner';
import { LoginTheme } from 'component/User/Login/types';

import { hasListEntries } from 'pages/hotels/reducer/AccommodationList/__helper/reducer';

import { Feature } from 'config/features';
import { defaultCountPerPage } from 'config/SearchForm/defaults';
import { SearchFormContext } from 'context/SearchFormContext';

import { usePagination } from 'hooks/form/search';
import { useFeature } from 'hooks/useFeature';

import { ReactComponent as IconInfoCircle } from 'media/svg/infoCircle.svg';
import { ReactComponent as IconStopwatch } from 'media/svg/stopwatch.svg';
import { ReactComponent as IconTwoPeopleClose } from 'media/svg/twoPeopleClose.svg';

import ErrorBoundary from 'utility/Debug/ErrorBoundary';
import { getDecimalNumber } from 'utility/Format/Number';
import { getFormattedPrice, PriceFormatter } from 'utility/Format/Price';
import { lazyRetry } from 'utility/Lazy/Retry';
import { CommunicationContext } from 'utility/Provide/Context/PageContext';
import { getURLParamValue } from 'utility/Provide/Url';
import FeatureHelper from 'utility/Toggle/Feature';
import {
    pushSearchListImpressions,
    pushVirtualPageView,
} from 'utility/Track/trackingHelper';

import { DefaultHotelSort } from 'types/legacy/backendInformation';
import { SortOrder } from 'types/Search/form';

import { ResponseStatus } from 'proto/Api/ResponseStatus';
import { HotelList } from 'proto/Hotel/Hotel';
import { ServiceInformation } from 'proto/ServiceInformation';
import { Tracking } from 'proto/Tracking/Tracking';

import {
    PORTAL_HOTEL_LIST_HEAD_ID,
    PORTAL_HOTEL_LIST_ID,
} from './__config/constants';
import { getActiveIndividualHotelFilter } from './__helper';
import DebugInfo from './__partials/DebugInfo';
import ListLoader from './__partials/ListLoader';
import NoResult from './__partials/NoResult';
import OutOfService from './__partials/OutOfService';
import { AdditionalRenderType, HotelListView } from './types';

import styles from './List.module.scss';

const ResultList = React.lazy(() =>
    lazyRetry(() => import('./__partials/ResultList'))
);

const Recommender = React.lazy(() =>
    lazyRetry(() => import('../../../Recommender/ListWrapper'))
);

const SCARCITY_RECENT_BOOKED_POSITION = 3;

const ResultListMemoized = React.memo(ResultList);
const ProgressMemoized = React.memo(Progress);

interface ListProps {
    view: HotelListView;
    accommodationList: HotelList;
    alternativeHotelList: HotelList;
    responseStatus: ResponseStatus;
    serviceInformation?: ServiceInformation;
    lastClickedHotel?: {
        id: number;
        name: string;
    };
    updateLastClickedHotel?: (
        id: number,
        name: string,
        isFeaturedHotel: boolean
    ) => void;
    tracking?: Tracking;
    transactionId?: string;
    isSemActive?: boolean;
    hasRecommender?: boolean;
}

const List: React.FC<ListProps> = ({
    view,
    accommodationList,
    alternativeHotelList,
    responseStatus,
    serviceInformation,
    updateLastClickedHotel,
    tracking,
    transactionId,
    isSemActive = false,
    hasRecommender = true,
}) => {
    const { t } = useTranslation();
    const isS3NudgingEnabled = useFeature(Feature.S3Nudging);
    const { activeFilterList } = useContext(SearchFormContext);
    const hasHotelList = hasListEntries(accommodationList);
    const hasAlternativeItems = hasListEntries(alternativeHotelList);
    const hasAlternativeHotelList =
        hasAlternativeItems || alternativeHotelList.count > 0;
    const hotelListTarget = document.getElementById(PORTAL_HOTEL_LIST_ID);
    const masterDataFromAlternativeHotel =
        alternativeHotelList.items[alternativeHotelList.itemOrder[0]]
            ?.masterData;
    const legacyFakeTiles = document.getElementById(
        'js-hotel-fake-tile-wrapper'
    );
    const hasLegacyFakeTiles = !!legacyFakeTiles ?? false;
    const [progressState, setProgressState] = useState<Status>(
        Status.Hibernate
    );
    const namespace = useContext(CommunicationContext);

    const defaultHotelSort: DefaultHotelSort =
        namespace.components.backendInformationFunc.getData('defaultHotelSort');

    const [pagination, setPagination] = usePagination();

    const isProgressBarActive = useRef(false); // Track if the progress bar is running

    //TODO: after re-implementation of search form in react, the region can be obtained from search context
    const recentBookingRegion =
        !isS3NudgingEnabled &&
        accommodationList.meta?.scarcity?.recentBookingRegion &&
        accommodationList.itemOrder.length > SCARCITY_RECENT_BOOKED_POSITION
            ? accommodationList.items[
                  accommodationList.itemOrder[SCARCITY_RECENT_BOOKED_POSITION]
              ]?.masterData?.region
            : undefined;

    const hasFeaturedHotel =
        accommodationList.items[0]?.flags?.isFeaturedHotel || false;

    const hasAccommodationList =
        accommodationList.itemOrder.length > 0 || hasFeaturedHotel;

    const onPageSwitch = (currentPage: number) => {
        const header = document.getElementById(PORTAL_HOTEL_LIST_HEAD_ID);

        setPagination((current) => ({
            pageCount: current.pageCount,
            currentPage,
        }));

        window.scrollTo({ top: header?.offsetTop ?? 0 });
    };

    const compoundHotelListItemCount =
        accommodationList.count + alternativeHotelList.count;

    const hasPagination =
        !!compoundHotelListItemCount &&
        compoundHotelListItemCount > defaultCountPerPage;

    const currentPage = useCallback(() => {
        return pagination.currentPage;
    }, [pagination.currentPage]);

    useEffect(() => {
        // Prevent reinitialization if already in Active state
        if (
            responseStatus === ResponseStatus.Pending &&
            isProgressBarActive.current
        ) {
            return;
        }
        switch (responseStatus) {
            case ResponseStatus.Empty:
                setProgressState(Status.Init);
                setTimeout(() => setProgressState(Status.Hibernate), 3000);
                if (legacyFakeTiles) {
                    legacyFakeTiles?.remove();
                }

                break;
            case ResponseStatus.Pending:
                isProgressBarActive.current = true;
                // reset progress state, because default case is not set or complete state use a quicker transition, which show the progress state sometimes initially on 70%
                setProgressState(Status.Init);
                setTimeout(() => setProgressState(Status.Active), 1000);
                if (
                    legacyFakeTiles &&
                    (hasHotelList || hasAlternativeHotelList)
                ) {
                    legacyFakeTiles?.remove();
                } else if (!!hotelListTarget && !!legacyFakeTiles) {
                    hotelListTarget.insertAdjacentHTML(
                        'afterend',
                        legacyFakeTiles.outerHTML
                    );
                }

                break;
            case ResponseStatus.Success:
                setProgressState(Status.Complete);
                setTimeout(() => setProgressState(Status.Hibernate), 3000);
                isProgressBarActive.current = false;
                legacyFakeTiles?.remove();

                break;
            case ResponseStatus.Error:
            case ResponseStatus.Timeout:
                setProgressState(Status.Complete);
                setTimeout(() => setProgressState(Status.Hibernate), 3000);
                isProgressBarActive.current = false;
                legacyFakeTiles?.remove();

                break;
            default:
                setProgressState(Status.Init);
        }
    }, [
        hotelListTarget,
        legacyFakeTiles,
        responseStatus,
        hasHotelList,
        hasAlternativeHotelList,
        hasAlternativeItems,
    ]);

    // push virtualPageView
    useEffect(() => {
        if (tracking?.pageView && responseStatus !== ResponseStatus.Pending) {
            pushVirtualPageView(tracking.pageView, 'hotelList');
        }
    }, [responseStatus, tracking?.pageView]);

    // push searchListImpression
    useEffect(() => {
        const impressions = accommodationList?.meta?.tracking?.impressions;

        if (impressions && responseStatus !== ResponseStatus.Pending) {
            impressions.forEach((impressionBatch) => {
                pushSearchListImpressions(impressionBatch, 'hotelList');
            });
        }
    }, [responseStatus, accommodationList?.meta?.tracking?.impressions]);

    const container = document.getElementById(PROMOTIONAL_VOUCHER_CONTAINER);

    const component =
        container && responseStatus === ResponseStatus.Success ? (
            <div className={styles.CouponVoyageBanner}>
                <CouponVoyageBanner
                    customClassList={{
                        Subline: styles.Subline,
                        Headline: styles.Headline,
                        Tagline: styles.Tagline,
                        CtaWrapper: styles.CtaWrapper,
                        Banner: styles.Banner,
                    }}
                    onFetch={() => {
                        document.dispatchEvent(
                            new CustomEvent('c24-travel-event-trigger-search')
                        );
                    }}
                />
            </div>
        ) : (
            <FeatureHelper name={Feature.S3LoginBanner}>
                <Login theme={LoginTheme.COLOR} />
            </FeatureHelper>
        );

    return (
        <ErrorBoundary options={{ fallback: <NoResult /> }}>
            <DebugInfo />
            <div
                className={styles.List}
                id="js-hotel-result-list"
                data-transaction-id={transactionId}
            >
                <FeatureHelper name={Feature.TransparencyInitiative}>
                    {/* TODO: future changes(SearchForm Refactoring) -> use SearchContext to get sorting */}
                    <Hint
                        sortType={
                            (getURLParamValue('sorting') ??
                                defaultHotelSort.sort) as SortOrder
                        }
                        currentStep={3}
                    />
                </FeatureHelper>
                <ProgressMemoized status={progressState} />
                {view === HotelListView.FakeTiles && !hasLegacyFakeTiles && (
                    <ListLoader count={defaultCountPerPage} />
                )}
                {view === HotelListView.NoResult && (
                    <>
                        <NoResult />
                        <TravelExpertWrapper
                            serviceInformation={serviceInformation}
                        />
                    </>
                )}
                {view === HotelListView.OutOfService && <OutOfService />}
                {view === HotelListView.ResultList && (
                    <Suspense fallback={<ListLoader />}>
                        {hasAccommodationList && (
                            <ResultListMemoized
                                accommodationList={accommodationList}
                                serviceCode={serviceInformation?.code}
                                transactionId={transactionId}
                                renderAdditionals={[
                                    {
                                        position: 0,
                                        type: AdditionalRenderType.Before,
                                        content: !isS3NudgingEnabled &&
                                            accommodationList.meta?.scarcity
                                                ?.highlyDemanded &&
                                            !sessionStorage.getItem(
                                                'hide_incentive_high_demand'
                                            ) && (
                                                <>
                                                    <Box
                                                        isScarcity={true}
                                                        type={BoxType.NEGATIVE}
                                                        icon={
                                                            <IconTwoPeopleClose />
                                                        }
                                                        onCloseCallback={() =>
                                                            sessionStorage.setItem(
                                                                'hide_incentive_high_demand',
                                                                '1'
                                                            )
                                                        }
                                                    >
                                                        <b>
                                                            {t(
                                                                'TRANS-high-demand'
                                                            )}
                                                        </b>
                                                        <p
                                                            dangerouslySetInnerHTML={{
                                                                __html: t(
                                                                    'TRANS-do-not-hesitate-limited-stock'
                                                                ),
                                                            }}
                                                        />
                                                    </Box>
                                                </>
                                            ),
                                    },
                                    {
                                        position: 2,
                                        type: AdditionalRenderType.Before,
                                        content: component,
                                    },
                                    {
                                        position: Math.min(
                                            3,
                                            accommodationList.itemOrder.length -
                                                1
                                        ),
                                        type: AdditionalRenderType.After,
                                        content: (
                                            <TravelExpertWrapper
                                                serviceInformation={
                                                    serviceInformation
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        position:
                                            SCARCITY_RECENT_BOOKED_POSITION,
                                        type: AdditionalRenderType.After,
                                        content: recentBookingRegion &&
                                            !sessionStorage.getItem(
                                                'hide_incentive_recentBookingRegion'
                                            ) && (
                                                <Box
                                                    isScarcity={true}
                                                    type={BoxType.NEGATIVE}
                                                    icon={<IconStopwatch />}
                                                    onCloseCallback={() =>
                                                        sessionStorage.setItem(
                                                            'hide_incentive_recentBookingRegion',
                                                            '1'
                                                        )
                                                    }
                                                >
                                                    <b>
                                                        {t(
                                                            'TRANS-popular-destination'
                                                        )}
                                                    </b>
                                                    <p
                                                        dangerouslySetInnerHTML={{
                                                            __html: t(
                                                                'TRANS-recently-booked-in-region-x-time-ago',
                                                                {
                                                                    region: recentBookingRegion,
                                                                    time: accommodationList
                                                                        .meta
                                                                        ?.scarcity
                                                                        ?.recentBookingRegion,
                                                                }
                                                            ),
                                                        }}
                                                    />
                                                </Box>
                                            ),
                                    },
                                    {
                                        position: 2,
                                        type: AdditionalRenderType.After,
                                        content: (
                                            <FeatureHelper
                                                name={
                                                    Feature.S3S4StiftungWarentestBanner
                                                }
                                            >
                                                <StiftungWarentest />
                                            </FeatureHelper>
                                        ),
                                    },
                                ]}
                                responseStatus={responseStatus}
                                updateLastClickedHotel={updateLastClickedHotel}
                                currentPage={currentPage()}
                                isSemActive={isSemActive}
                            />
                        )}

                        {hasAlternativeHotelList && (
                            <>
                                {!hasHotelList && currentPage() === 1 && (
                                    <NoResult />
                                )}
                                {((hasHotelList &&
                                    accommodationList.count <=
                                        currentPage() * defaultCountPerPage) ||
                                    (!hasHotelList && currentPage() === 1)) && (
                                    <Box
                                        type={BoxType.DEFAULT}
                                        customClassList={{
                                            Box: styles.AlternativeHotelListMessage,
                                            Icon: styles.Icon,
                                        }}
                                        icon={<IconInfoCircle />}
                                    >
                                        <p
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'TRANS-discover-more-accommodations',
                                                    {
                                                        filterList:
                                                            getActiveIndividualHotelFilter(
                                                                activeFilterList
                                                            ).join(', '),
                                                        resultCount:
                                                            getDecimalNumber(
                                                                alternativeHotelList.count,
                                                                0
                                                            ),
                                                        alternativeMinPrice:
                                                            getFormattedPrice(
                                                                alternativeHotelList.priceMin,
                                                                PriceFormatter.NoFractionDigits
                                                            ),
                                                        location:
                                                            masterDataFromAlternativeHotel?.region,
                                                    }
                                                ),
                                            }}
                                        />
                                    </Box>
                                )}
                                {hasAlternativeItems && (
                                    <ResultListMemoized
                                        accommodationList={alternativeHotelList}
                                        serviceCode={serviceInformation?.code}
                                        transactionId={transactionId}
                                        responseStatus={responseStatus}
                                        updateLastClickedHotel={
                                            updateLastClickedHotel
                                        }
                                        currentPage={currentPage()}
                                        isSemActive={isSemActive}
                                    />
                                )}
                            </>
                        )}
                        {hasRecommender && <Recommender />}
                        {hasPagination && (
                            <Pagination
                                initOnPage={currentPage()}
                                itemsPerPage={defaultCountPerPage}
                                maxItems={compoundHotelListItemCount}
                                onPageSwitch={onPageSwitch}
                            />
                        )}
                    </Suspense>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default List;
